import { CollaborationIcon } from '@blocksuite/icons/rc';
import { Entity, LiveData } from '@toeverything/infra';

import type { DocsService } from '../../doc';
import type { DocDisplayMetaService } from '../../doc-display-meta';
import type { PeopleSearchService } from '../services/people-search';
import type { QuickSearchService } from '../services/quick-search';
import type { QuickSearchItem } from '../types/item';

export interface PersonDocPayload {
  docId: string;
  userId: string;
  userName: string;
}
export function isPersonDocItem(
  item: QuickSearchItem
): item is QuickSearchItem<'people', PersonDocPayload> {
  return item.source === 'people';
}

export class PeopleQuickSearchSession extends Entity {
  constructor(
    private readonly people: PeopleSearchService,
    private readonly search: QuickSearchService,
    private readonly docs: DocsService,
    private readonly display: DocDisplayMetaService
  ) {
    super();
  }
  readonly query$ = new LiveData('');
  query(query: string) {
    this.query$.next(query);
  }
  readonly items$ = LiveData.computed(get => {
    const query = get(this.query$).trim().toLocaleLowerCase();
    const selected = get(this.search.personFilter$);
    if (!query && !selected) return [];
    const items: QuickSearchItem<'people', PersonDocPayload>[] = [];
    for (const person of get(this.people.people$)) {
      if (selected && selected.id !== person.id) continue;
      for (const docId of person.docIds) {
        const doc = get(this.docs.list.doc$(docId));
        if (!doc || get(doc.trash$)) continue;
        const rawTitle = get(doc.title$);
        const meta = this.display.getDocDisplayMeta(doc);
        const title = typeof meta.title === 'string' ? meta.title : rawTitle;
        if (
          query &&
          !(selected
            ? title.toLocaleLowerCase().includes(query)
            : person.name.toLocaleLowerCase().includes(query))
        )
          continue;
        items.push({
          id: `people:${person.id}:${docId}`,
          source: 'people',
          icon: CollaborationIcon,
          group: {
            id: 'people',
            label: { i18nKey: 'com.streamlain.quicksearch.people' },
            score: 20,
          },
          label: { title: person.name, subTitle: meta.title },
          score: 1,
          payload: { docId, userId: person.id, userName: person.name },
        });
      }
    }
    return items;
  });
}
